import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EncRelativeDateFilter } from '@mode-vegas/vegas';
import { DatePickerComponent } from '@mode/shared/ui';
import { formatInUTC } from '@mode/shared/util-js';
import { findOrFirst } from '../helpers/field-helpers';
import { LabeledMenuComponent } from '@mode/shared/ui';
import {
  DatePartOption,
  RangeOption,
  RelRange,
  datePartOptions,
  getRelativeRangePart,
  makeFilterFromTo,
  rangeOptions,
} from './helpers';

const today = new Date();
const unsetType: EncRelativeDateFilter.FromTo = { type: 'unset' };

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, DatePickerComponent, LabeledMenuComponent],
  selector: 'mode-relative-date-fromto',
  templateUrl: './relative-date-fromto.component.html',
  styleUrls: ['./relative-date-fromto.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelativeDateFromToComponent implements OnChanges {
  @Input() unsetLabel = 'Unset';
  @Input() fixedDefault: number | undefined;
  @Input() fromto: EncRelativeDateFilter.FromTo = unsetType;
  @Output() fromtoChange = new EventEmitter<EncRelativeDateFilter.FromTo | undefined>();

  rangeOptions = rangeOptions;
  selectedRange? = rangeOptions[0];

  datePartOptions = datePartOptions;
  selectedDatePart? = datePartOptions[0];

  fixedPlaceholder = formatInUTC(today, 'MM/dd/yyyy');
  fixedMinDate: Date | null = today;
  fixed: Date | null = today;
  value: number | null = 1;

  readonlyTooltipContent = `"Start of This" and "End of This" have a preset of one`;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.unsetLabel) {
      this.rangeOptions = rangeOptions.map((opt) =>
        opt.value === RelRange.Unset ? { ...opt, name: this.unsetLabel ?? 'Unset' } : opt
      );
    }

    const fromto = this.fromto ?? unsetType;

    const range = getRelativeRangePart(fromto);
    this.selectedRange = findOrFirst(this.rangeOptions, (value) => {
      return value === range;
    });

    // Fixed date
    if (fromto.type === 'fixed') {
      this.fixed = new Date(fromto.value);
    }

    // Fixed date placeholder
    if (typeof changes.fixedDefault?.currentValue === 'number') {
      const timestamp = changes.fixedDefault.currentValue;
      this.fixedPlaceholder = formatInUTC(new Date(timestamp), 'MM/dd/yyyy');
      this.fixedMinDate = new Date(timestamp);

      // Update our default of `today` to the provided default now that we have it
      if (this.fixed === today) {
        this.fixed = this.fixedMinDate;
      }
    }

    // Relative date
    if (fromto.type === 'relative') {
      this.value = fromto.value;
      this.selectedDatePart = findOrFirst(datePartOptions, (value) => {
        return value === fromto.part;
      });
    }
  }

  onChangeRange(range: RangeOption): void {
    this.selectedRange = range;

    // Apply a fixed value of 1 here since we don't allow changes
    if (this.valueIsReadonly) {
      this.value = 1;
    }

    this.onChange();
  }

  onChangePart(part: DatePartOption): void {
    this.selectedDatePart = part;
    this.onChange();
  }

  onChangeValue(value: number | null): void {
    this.value = value;
    this.onChange();
  }

  onChangeFixed(fixed: Date | null): void {
    this.fixed = fixed;
    this.onChange();
  }

  onChange(): void {
    // Should always have a selection for these
    if (!(this.selectedRange && this.selectedDatePart)) {
      return this.fromtoChange.emit(undefined);
    }

    try {
      const fixedValue = this.fixed != null ? +this.fixed : null;
      this.fromtoChange.emit(
        makeFilterFromTo(this.selectedRange.value, this.selectedDatePart.value, this.value, fixedValue)
      );
    } catch (error: unknown) {
      this.fromtoChange.emit(undefined);
      if (error instanceof Error) {
        console.warn(error.message);
      }
    }
  }

  get showFixed(): boolean {
    return this.selectedRange?.value === RelRange.Fixed;
  }

  get showValueAndPart(): boolean {
    switch (this.selectedRange?.value) {
      case RelRange.Fixed:
      case RelRange.Unset: {
        return false;
      }
      default: {
        return true;
      }
    }
  }

  get valueIsReadonly(): boolean {
    switch (this.selectedRange?.value) {
      case RelRange.StartOfThis:
      case RelRange.EndOfThis: {
        return true;
      }
      default: {
        return false;
      }
    }
  }
}
